// frontend/src/hooks/useManualStrategyActions.js
import { useRef, useState } from 'react';
import axios from 'axios';
import { manualLineDeleteDisabledReason } from '../utils/manualBoxLine';

export default function useManualStrategyActions(control, onStrategyChanged) {
  const [pendingId, setPendingId] = useState(null);
  const pendingRef = useRef(false);
  const [message, setMessage] = useState('');
  const isManual = control?.mode === 'MANUAL_MODE';
  const canManage = Boolean(control?.can_manage);

  const errorMessage = (err) => {
    const detail = err?.response?.data?.detail;
    return (
      detail?.message || (typeof detail === 'string' ? detail : err.message)
    );
  };

  const handleStrategy = async (row, action) => {
    if (!canManage || pendingId !== null || pendingRef.current) return false;
    if (action === 'delete-line') {
      const reason = manualLineDeleteDisabledReason(control, row);
      if (reason) {
        setMessage(reason);
        return false;
      }
    }
    const price = Number(row.close_box);
    const direction = row.is_top ? '매수' : '매도';
    const prompt =
      action === 'on'
        ? `${direction} 진입선 ${price.toFixed(2)}\n진입 기준 ${(price + (row.is_top ? 0.25 : -0.25)).toFixed(2)} / 초기 손절 ${(price + (row.is_top ? -10 : 10)).toFixed(2)}\n전략을 생성·활성화할까요? 조건 충족 시 자동 주문이 실행될 수 있습니다.`
        : action === 'off'
          ? `${price.toFixed(2)} 진입선을 OFF할까요? 신규 진입·재진입이 중단되며, 보유분의 자동 손절·익절·청산은 계속됩니다.`
          : action === 'delete-line'
            ? `${row.is_top ? '상단' : '하단'} ${price.toFixed(2)} 수동 입력 라인을 삭제할까요?\n연결된 전략과 해당 전략의 주문·체결 이력도 함께 삭제됩니다. 삭제한 데이터는 복구할 수 없습니다.`
            : `${price.toFixed(2)} 라인의 전략과 주문·체결 이력을 삭제할까요? 원본 박스라인은 유지되며 자동으로 전략을 다시 생성하지 않습니다. 삭제한 데이터는 복구할 수 없습니다.`;
    if (!window.confirm(prompt)) return false;
    pendingRef.current = true;
    setPendingId(row.id);
    setMessage('');
    const config = {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      },
    };
    const base = `${process.env.REACT_APP_API_URL}/boxlines/${row.id}`;
    try {
      if (action === 'on')
        await axios.post(`${base}/manual-activation`, {}, config);
      else if (action === 'delete')
        await axios.delete(`${base}/manual-strategy`, config);
      else if (action === 'delete-line')
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/boxlines/manual/${row.id}`,
          config,
        );
      else {
        try {
          await axios.post(
            `${base}/manual-off`,
            { confirm_open_position: false },
            config,
          );
        } catch (err) {
          if (
            err?.response?.data?.detail?.code !==
            'open_position_confirmation_required'
          )
            throw err;
          if (!window.confirm(err.response.data.detail.message)) return;
          await axios.post(
            `${base}/manual-off`,
            { confirm_open_position: true },
            config,
          );
        }
      }
      setMessage(
        action === 'on'
          ? '전략을 활성화했습니다.'
          : action === 'off'
            ? '진입선을 OFF했습니다.'
            : action === 'delete-line'
              ? '수동 입력 라인과 연결된 전략을 삭제했습니다.'
              : '전략을 삭제했습니다.',
      );
      onStrategyChanged?.();
      return true;
    } catch (err) {
      setMessage(errorMessage(err));
      onStrategyChanged?.();
      return false;
    } finally {
      pendingRef.current = false;
      setPendingId(null);
    }
  };

  return {
    canManage,
    isManual,
    pendingId,
    message,
    errorMessage,
    handleStrategy,
  };
}
